/**
 * CSRF token for mutating requests.
 *
 * social-admin keeps Spring Security's CSRF protection. The token is read from the
 * `XSRF-TOKEN` cookie when the server exposes one; otherwise it is fetched once from
 * `GET /api/auth/csrf` and cached until the session changes (sign-in and sign-out rotate it).
 */
export const CSRF_COOKIE_NAME = 'XSRF-TOKEN';
export const DEFAULT_CSRF_HEADER = 'X-XSRF-TOKEN';
export const CSRF_ENDPOINT = '/api/auth/csrf';

export interface CsrfToken {
  headerName: string;
  token: string;
}

let cached: CsrfToken | null = null;
let inflight: Promise<CsrfToken> | null = null;

export async function getCsrfToken(fetchImpl: typeof fetch = fetch): Promise<CsrfToken> {
  const fromCookie = readCookie(CSRF_COOKIE_NAME);
  if (fromCookie) return { headerName: DEFAULT_CSRF_HEADER, token: fromCookie };
  if (cached) return cached;
  inflight ??= fetchCsrfToken(fetchImpl).finally(() => {
    inflight = null;
  });
  return inflight;
}

/** Forgets the cached token; the next mutating request fetches a fresh one. */
export function resetCsrfToken(): void {
  cached = null;
  inflight = null;
}

async function fetchCsrfToken(fetchImpl: typeof fetch): Promise<CsrfToken> {
  const response = await fetchImpl(CSRF_ENDPOINT, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
  });
  if (!response.ok) throw new Error(`CSRF token request failed with ${response.status}`);
  const body: unknown = await response.json();
  const token = parseCsrfBody(body);
  // The server may have set the cookie as a side effect; prefer it from now on.
  const fromCookie = readCookie(CSRF_COOKIE_NAME);
  cached = fromCookie ? { headerName: token.headerName, token: fromCookie } : token;
  return cached;
}

function parseCsrfBody(body: unknown): CsrfToken {
  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;
    const token = record.token;
    const headerName = record.headerName ?? record.header_name;
    if (typeof token === 'string' && token !== '') {
      return {
        token,
        headerName:
          typeof headerName === 'string' && headerName !== '' ? headerName : DEFAULT_CSRF_HEADER,
      };
    }
  }
  throw new Error('CSRF token response did not contain a token');
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  for (const part of document.cookie.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) return decodeURIComponent(rest.join('='));
  }
  return null;
}
